/* eslint-disable complexity */
import React from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { generateDetailUrl } from '../../Stocks/Listing/constants'
import { TAssetDetailsData } from '@/components/Stocks/Widgets/types'
import { useUserRQInfo } from '@/components/Common/user-provider/user-provider'
import { KeyValueTableExpandable } from '@/stories/components/INStocksDetail/KeyValueTableExpandable'
import { RUPEE_SYMBOL } from '@/root/constants'
import MarketBreakup from '@/stories/components/INStocksDetail/MarketBreakup'
import CompanyInformation from '@/stories/components/USStocksDetail/CompanyInformation'
import Link from '@/components/Common/Link'
import IndexCalculator from '@/components/Stocks/Widgets/IndexCalculator'
import TodaysPositions from '@/components/Stocks/TransactionSystem/BuySell/TodaysPositions'
import INStocksOverview from '@/stories/components/INStocksDetail/INStocksOverview'
import { IHeaders } from '@/components/Stocks/Pages/INStockDetailPage/types'
import NewPerformance from '@/stories/components/Common/PerformanceWidget'
import INDStocksInsights from '@/components/Stocks/Widgets/INDStocksInsights'
import { generateEtfFaqsV2 } from '@/components/Stocks/Utils/generateStockFaqs'
import { StrapiV4SectionFAQs } from '@/stories/strapi-4/widget-components/faqs-section/faqs-section'
import Infographics from '@/components/Common/Infographics'
import { SectionWrapper } from '@/components/ScrollAwareV2/SectionWrapper'

const INStockHoldingDetailsCard = dynamic(
  () =>
    import(
      '@/components/Stocks/TransactionSystem/BuySell/INStockHoldingDetailsCard'
    ),
  {
    ssr: false,
  },
)
const MarketDepthWidget = dynamic(
  () =>
    import('@/components/Stocks/TransactionSystem/BuySell/MarketDepthWidget'),
  {
    ssr: false,
  },
)

/* eslint-disable max-lines-per-function */

const ETFDetail = (
  props?: TAssetDetailsData & {
    stockApiLoading: boolean
    isMobile: boolean | null
    headers?: IHeaders
  },
) => {
  const { isNotAuthenticated, isAuthenticated } = useUserRQInfo()

  const router = useRouter()
  const { query = {} } = router
  const { scrip_asset_type } = query

  const {
    catalog = {},
    graph = {},
    investment_info = {},
    account_info = {},
    isMobile,
    stockApiLoading,
    headers,
    metadata,
    market_status = {},
  } = props ?? {}

  const { entity_basic = {}, entity_stats = {}, etf_details = {} } = catalog
  const { holdings, intraday_position } =
    investment_info.demat?.investment_summary ?? {}

  const { performance, live_price = '' } = entity_stats

  const {
    market_depth_socket_path = '',
    name = '',
    ind_key = '',
    socket_path,
  } = entity_basic

  const { is_demat_user, is_whitelisted } = account_info

  const stockData = {
    ...entity_basic,
    companyCode: entity_basic.ind_key,
    percent_change: entity_stats.day_change_percentage,
    ...graph,
    chartData: graph.candles ?? [],
    live_price,
    fallback: '',
    trading_symbol: entity_basic.symbol ?? ind_key,
    day_change: entity_stats.day_change,
    isMarketOpen: market_status?.current_market_status !== 'close',
    last_updated: entity_stats.last_updated_v2,
  }

  const {
    peers: etfComposition,
    benchmarks,
    market_cap_break_up: etfMCBU,
    sector_break_up: etfSBU,
    stock_composition: etfStockComposition,
    summary,
    stats,
    research: entityDetailsResearch,
    peer_rank,
    total_peers,
    insights_data,
  } = etf_details

  const { infographics } = entityDetailsResearch ?? {}
  const composedData = dataList => {
    const list = dataList?.map(item => {
      return {
        title: item.stock_name ?? '',
        title_logo: item.stock_logo ?? '',
        value: Math.abs(item.one_day_percentage_return ?? 0),
        navlink: generateDetailUrl({
          stock_name: item.stock_name ?? '',
          company_code: item.ind_key,
          link: item.relativepath,
        }),
        valueClassNames:
          (item.one_day_percentage_return ?? 0) < 0
            ? "text-brand-red-cinnabar before:content-['▼_'] after:content-['%']"
            : "text-brand-green-dark before:content-['▲_'] after:content-['%']",
      }
    })
    return list
  }

  const etfStockCompostionData = composedData(etfStockComposition)
  const showDematPersuasion =
    isAuthenticated && is_whitelisted && !is_demat_user

  const faqs = generateEtfFaqsV2({
    ...entity_basic,
    ...entity_stats,
    ...performance,
    ...etf_details,
  })

  const faqSection = {
    heading: `FAQs on ${entity_basic.name}`,
    faqs,
  }

  return (
    <div className="flex w-full flex-col gap-y-16">
      <SectionWrapper id="share-price">
        <INStocksOverview
          {...stockData}
          firebase_path={socket_path}
          showLoginWidget={isNotAuthenticated}
          showDematPersuasion={showDematPersuasion}
          graphLoading={!!stockApiLoading}
          loggedIn={isAuthenticated}
          isMobile={isMobile}
          headers={headers}
          metadata={metadata}
          isDemat={account_info.is_demat_user}
          entityClass={catalog.entity_class}
        />
        {intraday_position && scrip_asset_type === 'intraday' && (
          <TodaysPositions
            data={intraday_position}
            ind_key={entity_basic.ind_key}
          />
        )}

        {holdings && <INStockHoldingDetailsCard holdings={holdings} />}

        {is_demat_user && market_depth_socket_path && (
          <MarketDepthWidget
            socket_path={market_depth_socket_path}
            market_status={props?.market_status}
          />
        )}

        {performance && (
          <NewPerformance
            {...performance}
            tooltip="Performance"
            heading={`${name} Performance`}
          />
        )}
      </SectionWrapper>

      <SectionWrapper id="fundamentals">
        {!!stats && (
          <section>
            <KeyValueTableExpandable
              headingClassName="!pb-6 !border-b-[0.5px] w-full lg:text-2xl mb-2"
              containerClass="!mt-0"
              childClass="!p-0"
              minToShow={5}
              data={[
                {
                  title: 'Expense Ratio',
                  value: `${stats.expense_ratio}%`,
                  valueClassNames: 'text-right',
                },
                {
                  title: 'Avg. PE ratio',
                  value: stats.avg_pe === 0 ? 'NA' : stats.avg_pe,
                  valueClassNames: 'text-right',
                },
                {
                  title: 'Avg. PB ratio',
                  value: stats.avg_pb === 0 ? 'NA' : stats.avg_pb,
                  valueClassNames: 'text-right',
                },
                {
                  title: 'AUM',
                  value: `${RUPEE_SYMBOL}${stats.aum} Cr.`,
                  valueClassNames: 'text-right',
                },
              ]}
              sectionHeading={`${name} Fundamentals`}
            />
          </section>
        )}
        {!!etfStockCompostionData?.length && (
          <section>
            <KeyValueTableExpandable
              headingClassName="!pb-6 !border-b-[0.5px] w-full lg:text-2xl mb-2"
              containerClass="!mt-0"
              minToShow={5}
              data={etfStockCompostionData}
              col1heading="Companies"
              col2heading="1D Returns"
              sectionHeading={`Companies in ${name}`}
              childClass="!p-0"
            />
          </section>
        )}
        {!!etfMCBU?.length && !!etfSBU?.length && (
          <section id="marketBreakup">
            <MarketBreakup market_cap={etfMCBU} sector_breakup={etfSBU} />
          </section>
        )}
        <IndexCalculator
          benchmarks={benchmarks ?? []}
          name={name}
          peer_rank={peer_rank}
          total_peers={total_peers}
          etfs_composition={etfComposition ?? []}
          childClass="!p-0"
          containerClassName="!m-0"
          headingClassName="!pb-6 !border-b-[0.5px] w-full lg:text-2xl !mb-6"
          mode="etf"
        />
      </SectionWrapper>

      <SectionWrapper id="news">
        {insights_data && (
          <INDStocksInsights
            data={insights_data}
            showDesc={!isMobile}
            itemClassName="!mb-0"
          />
        )}

        {infographics?.heading && <Infographics {...infographics} />}
      </SectionWrapper>

      <SectionWrapper id="company-info">
        {summary?.description && (
          <CompanyInformation
            containerClass="!m-0"
            childClass="!p-0"
            headingClassName="!pb-6 !border-b-[0.5px] w-full lg:text-2xl mb-2"
            data={{
              title: `About ${name}`,
              description: summary.description,
              dataList: [
                {
                  title: 'Exchange',
                  subtitle: entity_basic.exchange ?? '',
                },
                {
                  title: 'E-voting on shares',
                  subtitle: (
                    <Link
                      href="https://evoting.cdslindia.com/Evoting/EvotingLogin"
                      className="text-brand-blue-azureRadiance"
                      openInNewTab
                    >
                      Click here to vote
                    </Link>
                  ),
                },
              ],
            }}
          />
        )}
      </SectionWrapper>
      <SectionWrapper id="faqs">
        {!!faqSection.faqs.length && (
          <StrapiV4SectionFAQs
            faq_section={faqSection}
            variant="v1"
            addFontSize="size-24"
          />
        )}
      </SectionWrapper>
    </div>
  )
}

export default ETFDetail
